import ChevronDown from "@/assets/images/icons/chevron-down.svg";
import { BreadcrumbHeader } from "@/components/BreadcrumbHeader";
import { CoursePagePremiumLoader } from "@/components/course/CoursePagePremiumLoader";
import ExternalLink from "@/components/ExternalLink";
import { ContentWrapper, ErrorDisplay, Popover } from "@/components/ui";
import { SkillPathwayThumbnail } from "@/components/ui/SkillPathwayThumbnail";
import { COURSE_THUMBNAIL_ASPECT } from "@/components/ui/CourseThumbnailCard";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAuth } from "@/lib/AuthContext";
import { useDraftViewer } from "@/hooks/useDraftViewer";
import { skillPathwayRoute } from "@/lib/skillPathwayNavigation";
import { useCurrentUrl } from "@/lib/useCurrentPath";
import { CourseStatus } from "@/lib/constants";
import { getCourseBySlug } from "@/supabase/services/courses";
import {
  fetchSkillPathwaysForDashboard,
  fetchUserCourseProgress,
} from "@/supabase/services/optimizedCourse";
import type {
  SkillLevel,
  SkillPathwayCourse,
} from "@/supabase/services/skillPathways";
import {
  fetchSiblingSkillPathwaysByPathwaySlug,
  getSkillPathwayBySlug,
} from "@/supabase/services/skillPathways";
import { mixpanelService } from "@/services/mixpanel.service";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import MusicIcon from "@/assets/images/icons/music-icon.svg";
import PlayCircleIcon from "@/assets/images/icons/play-circle.svg";
import TeacherIcon from "@/assets/images/icons/teacher-icon.svg";
import { Platform, ScrollView, View } from "react-native";
import { Image, XStack, YStack, useMedia } from "tamagui";
import {
  BodyText,
  CardTitle,
  ContentTitle,
  EyebrowText,
  PageTitle,
} from "@/components/ui/Typography";

function levelLabel(level: SkillLevel): string {
  if (level === "beginner") return "Beginner";
  if (level === "intermediate") return "Intermediate";
  return "Advanced";
}

function AnimatedChevron({ open }: { open: boolean }) {
  return (
    <XStack
      w={36}
      h={36}
      ai="center"
      jc="center"
      br={18}
      borderWidth={1}
      borderColor="rgba(0,0,0,0.08)"
      bg="#fff"
    >
      <View
        style={{
          transform: [{ rotate: open ? "360deg" : "180deg" }],
          // @ts-ignore
          transition: "transform 300ms ease-in-out",
        }}
      >
        <ChevronDown width={20} height={20} />
      </View>
    </XStack>
  );
}

function Collapsible({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<View>(null);
  const [h, setH] = useState<number | "auto">(open ? "auto" : 0);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (open) {
      ref.current?.measure?.((_x, _y, _w, mh) => {
        setH(mh);
        setTimeout(() => setH("auto"), 320);
      });
    } else {
      ref.current?.measure?.((_x, _y, _w, mh) => {
        setH(mh);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setH(0));
        });
      });
    }
  }, [open]);

  return (
    <View
      style={{
        overflow: "hidden",
        height: h,
        // @ts-ignore web CSS
        transition: "height 300ms ease-in-out",
      }}
    >
      <View ref={ref}>{children}</View>
    </View>
  );
}

type PathwayCourseCardProps = {
  course: SkillPathwayCourse;
  progressPercentage?: number;
  href: string;
};

function PathwayCourseCard({
  course,
  progressPercentage,
  href,
}: PathwayCourseCardProps) {
  const media = useMedia();
  const isMobile = !media.gtSm;
  const progress = progressPercentage ?? 0;
  const progressBarHeight = media.gtMd ? 6 : media.gtSm ? 4 : 2;
  const lessonsCount = course.no_of_lessons ?? 0;
  const gpsCount = course.no_of_gps ?? 0;
  const teacherLabel = course.teacher_name;
  const levelAccentColor =
    course.level === "beginner"
      ? "rgba(190, 244, 215, 1)"
      : course.level === "intermediate"
        ? "rgba(254, 230, 133, 1)"
        : "rgba(196, 180, 255, 1)";

  return (
    <ExternalLink
      url={href}
    >
    <XStack
      bg="#fff"
      br={10}
      boxShadow="0 1px 6px rgba(0,0,0,0.08)"
      overflow="hidden"
      w="100%"
      style={{ cursor: "pointer" }}
    >
      {/* Left: thumbnail + progress bar */}
      <YStack
        w={160}
        $gtSm={{ w: 229 }}
        $gtMd={{ w: 267 }}
        bg="#eee"
        overflow="hidden"
        borderTopRightRadius={10}
        borderBottomRightRadius={10}
      >
        {course.thumbnail ? (
          <Image
            source={{ uri: course.thumbnail }}
            w="100%"
            aspectRatio={COURSE_THUMBNAIL_ASPECT}
            resizeMode="cover"
          />
        ) : (
          <YStack w="100%" aspectRatio={COURSE_THUMBNAIL_ASPECT} bg="#b0d0ff" />
        )}
        <XStack
          h={progressBarHeight}
          backgroundColor="#D2CCC5"
          overflow="hidden"
        >
          <YStack
            h="100%"
            backgroundColor="$accent"
            borderRadius={30}
            w={`${progress}%`}
          />
        </XStack>
      </YStack>

      {/* Right: content */}
      <YStack flex={1} p={16} gap={12}>
        <CardTitle
          color="#0D0D0D"
          numberOfLines={isMobile ? 3 : 1}
          ellipsizeMode="tail"
        >
          {course.title}
        </CardTitle>
        {!isMobile && (
          <XStack ai="center" gap={16} overflow="hidden">
            {teacherLabel ? (
              <XStack ai="center" gap={4} flexShrink={1} overflow="hidden">
                <TeacherIcon
                  width={16}
                  height={16}
                  color="#795C40"
                  style={{ flexShrink: 0 }}
                />
                <EyebrowText
                  color="#795C40"
                  numberOfLines={1}
                >
                  {teacherLabel}
                </EyebrowText>
              </XStack>
            ) : null}
            {(lessonsCount > 0 || gpsCount > 0) && (
              <XStack ai="center" gap={4} flexShrink={0}>
                <PlayCircleIcon
                  width={16}
                  height={16}
                  color="#795C40"
                  style={{ flexShrink: 0 }}
                />
                <EyebrowText
                  color="#795C40"
                  numberOfLines={1}
                >
                  {(() => {
                    const parts: string[] = [];
                    if (lessonsCount > 0) {
                      parts.push(
                        lessonsCount === 1
                          ? "1 Lesson"
                          : `${lessonsCount} Lessons`,
                      );
                    }
                    if (gpsCount > 0) {
                      parts.push(`${gpsCount} GPS`);
                    }
                    return parts.join(" · ");
                  })()}
                </EyebrowText>
              </XStack>
            )}
          </XStack>
        )}
        {!isMobile && course.summary ? (
          <BodyText
            color="#5C5854"
            numberOfLines={media.gtMd ? 3 : 2}
            ellipsizeMode="tail"
            style={{ overflow: "hidden" }}
          >
            {course.summary}
          </BodyText>
        ) : null}
      </YStack>
      <YStack
        w={8}
        bg={levelAccentColor}
        borderTopRightRadius={10}
        borderBottomRightRadius={10}
      />
    </XStack>
    </ExternalLink>
  );
}

export default function SkillPathwayScreen() {
  const {
    "skill-pathway-slug": pathwaySlug,
    courseSlug,
    courseTitle,
  } = useLocalSearchParams<{
    "skill-pathway-slug": string;
    courseSlug?: string;
    courseTitle?: string;
  }>();
  const slug = String(pathwaySlug || "");
  const { fullPath } = useCurrentUrl();

  // Visibility is derived from the draft_viewers allowlist, not a URL param.
  const canViewDrafts = useDraftViewer();
  const minStatus = canViewDrafts
    ? CourseStatus.Retired
    : CourseStatus.Published;
  const { user } = useAuth();
  const router = useRouter();

  const hasReferrerCourseSlug =
    typeof courseSlug === "string" && courseSlug.trim().length > 0;

  // Visit tracking lives further down, after the pathway query is declared, so
  // the event can carry the pathway title once loaded (key-guarded per slug).
  const media = useMedia();
  const [pathwayMenuOpen, setPathwayMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<SkillLevel, boolean>>({
    beginner: true,
    intermediate: true,
    advanced: true,
  });

  const toggleLevel = useCallback((level: SkillLevel) => {
    setExpanded((prev) => ({ ...prev, [level]: !prev[level] }));
  }, []);

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ["skillPathway", slug, minStatus],
    queryFn: () => getSkillPathwayBySlug(slug, minStatus),
    enabled: !!slug,
    placeholderData: keepPreviousData,
  });

  const { data: referrerCourse } = useQuery({
    queryKey: [
      "courseBySlug",
      typeof courseSlug === "string" ? courseSlug : null,
    ],
    queryFn: () => getCourseBySlug(String(courseSlug)),
    enabled: typeof courseSlug === "string" && courseSlug.length > 0,
  });

  const { data: referrerPathways = [] } = useQuery({
    queryKey: ["skillPathwaysForDashboard", referrerCourse?.id ?? null],
    queryFn: () => fetchSkillPathwaysForDashboard(referrerCourse!.id),
    enabled: !!referrerCourse?.id,
  });

  const { data: siblingPathways = [] } = useQuery({
    queryKey: ["siblingSkillPathways", slug],
    queryFn: () => fetchSiblingSkillPathwaysByPathwaySlug(slug),
    enabled: !!slug && !hasReferrerCourseSlug,
  });

  const allPathways = useMemo(() => {
    if (hasReferrerCourseSlug) {
      return referrerPathways.flatMap((s) => s.pathways);
    }
    return siblingPathways;
  }, [hasReferrerCourseSlug, referrerPathways, siblingPathways]);

  const pathwayOptions = useMemo(() => {
    if (!allPathways.length) return [];
    const unique = new Map<string, { slug: string; title: string }>();
    for (const p of allPathways) {
      if (!p?.slug) continue;
      unique.set(p.slug, { slug: p.slug, title: p.title });
    }
    return [...unique.values()];
  }, [allPathways]);

  const goToPathwaySlug = useCallback(
    (nextSlug: string) => {
      setPathwayMenuOpen(false);
      if (nextSlug === slug) return;
      const cs = typeof courseSlug === "string" ? courseSlug : "";
      const ct = typeof courseTitle === "string" ? courseTitle : "";
      // No min_status in the URL — the destination derives visibility from the
      // draft_viewers allowlist.
      router.replace(skillPathwayRoute(nextSlug, cs, ct) as any);
    },
    [slug, courseSlug, courseTitle, router],
  );

  /** Match URL so hero (icon/color) stays correct while pathway query loads or placeholder data is stale */
  const pathwayFromReferrer = allPathways.find((p) => p.slug === slug);
  const currentPathwayInfo = pathwayFromReferrer;

  const listMatchesRoute = data != null && data.slug === slug;
  const displayTitle =
    listMatchesRoute
      ? data.title
      : pathwayFromReferrer?.title ?? data?.title ?? "\u2026";

  useDocumentTitle(displayTitle !== "\u2026" ? displayTitle : null);

  // Fire once per pathway slug, after the query settles so the event carries
  // the Skill Path Title (slug-only if the load failed). The key guard keeps
  // user/auth changes from double-counting the same visit.
  const pathwayVisitTrackedSlug = useRef<string | null>(null);
  useEffect(() => {
    if (!slug || isPending) return;
    if (pathwayVisitTrackedSlug.current === slug) return;
    pathwayVisitTrackedSlug.current = slug;
    const cs =
      typeof courseSlug === "string"
        ? courseSlug
        : Array.isArray(courseSlug)
          ? courseSlug[0]
          : undefined;
    const referrerCourseSlug =
      cs && String(cs).trim().length > 0 ? String(cs).trim() : undefined;
    mixpanelService.trackSkillPathwayVisit(
      slug,
      referrerCourseSlug,
      user,
      fullPath,
      listMatchesRoute ? data?.title : pathwayFromReferrer?.title ?? data?.title,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isPending, courseSlug, fullPath, user?.id, data?.title]);

  const listLoading = isPending || (isFetching && !listMatchesRoute);

  const pathwayHasCourses =
    listMatchesRoute && data != null && data.courses.length > 0;

  const pathwayCourseIds = useMemo(() => {
    if (!data || data.slug !== slug) return [];
    return data.courses.map((c) => c.id);
  }, [data, slug]);
  const { data: courseProgressMap = {} } = useQuery({
    queryKey: ["userCourseProgress", user?.id ?? null, pathwayCourseIds],
    queryFn: () => fetchUserCourseProgress(user?.id ?? null, pathwayCourseIds),
    enabled: !!user?.id && pathwayCourseIds.length > 0,
  });

  const coursesByLevel = useMemo(() => {
    const map: Record<SkillLevel, SkillPathwayCourse[]> = {
      beginner: [],
      intermediate: [],
      advanced: [],
    };
    if (!data) return map;
    for (const course of data.courses) {
      map[course.level].push(course);
    }
    return map;
  }, [data]);

  if (error) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay error={error as Error} onRetry={refetch} />
      </YStack>
    );
  }

  if (!slug) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay error={new Error("Skill pathway not found")} />
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg">
      <ScrollView
        contentContainerStyle={{ paddingBottom: 32 }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
      >
        <ContentWrapper>
          {hasReferrerCourseSlug ? (
            <BreadcrumbHeader
              courseSlug={String(courseSlug)}
              courseTitle={
                typeof courseTitle === "string" && courseTitle.trim().length > 0
                  ? courseTitle
                  : referrerCourse?.title ?? ""
              }
            />
          ) : null}
          <YStack
            w="100%"
            px="$4"
            maw={1140}
            mx="auto"
            ai="flex-start"
            py={14}
            gap={28}
            $gtSm={{ py: 18, gap: 36 }}
            $gtMd={{ py: 20, gap: 47 }}
            zIndex={1000}
          >
            <XStack
              w="100%"
              ai="center"
              gap={14}
              $gtSm={{ gap: 18 }}
              $gtMd={{ gap: 24 }}
            >
              <YStack
                w={56}
                h={56}
                br={8}
                bg={currentPathwayInfo?.color || "#FDE9DB"}
                jc="center"
                ai="center"
                flexShrink={0}
                $gtSm={{ w: 64, h: 64, br: 9 }}
                $gtMd={{ w: 80, h: 80, br: 10 }}
              >
                <YStack
                  w={32}
                  h={32}
                  $gtSm={{ w: 40, h: 40 }}
                  $gtMd={{ w: 48, h: 48 }}
                  jc="center"
                  ai="center"
                >
                  <SkillPathwayThumbnail
                    uri={currentPathwayInfo?.thumbnail}
                    size={media.gtMd ? 48 : media.gtSm ? 40 : 32}
                    fallback={
                      <MusicIcon
                        width={media.gtMd ? 48 : media.gtSm ? 40 : 32}
                        height={media.gtMd ? 48 : media.gtSm ? 40 : 32}
                        color="#5C5854"
                      />
                    }
                  />
                </YStack>
              </YStack>
              <YStack
                flex={1}
                ai="flex-start"
                gap={8}
                minWidth={0}
                $gtSm={{ gap: 12 }}
                $gtMd={{ gap: 16 }}
              >
                <PageTitle
                  letterSpacing={0}
                  color="#0D0D0D"
                  ta="left"
                >
                  {displayTitle}
                </PageTitle>
                <Popover
                  open={pathwayMenuOpen}
                  onOpenChange={setPathwayMenuOpen}
                  placement="bottom-start"
                  width={148}
                  maxWidth={148}
                  content={
                    <YStack pt={16} pb={16} pl={16} pr={24} gap={12}>
                      {pathwayOptions.map((p) => {
                        const isActive = p.slug === slug;
                        return (
                          <XStack
                            key={p.slug}
                            alignSelf="flex-start"
                            py={2}
                            px={4}
                            jc="center"
                            ai="center"
                            br={4}
                            bg={isActive ? "#F4F1ED" : "transparent"}
                            hoverStyle={{ backgroundColor: "#F4F1ED" }}
                            cursor="pointer"
                            onPress={() => goToPathwaySlug(p.slug)}
                          >
                            <EyebrowText
                              color="#0D0D0D"
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {p.title}
                            </EyebrowText>
                          </XStack>
                        );
                      })}
                    </YStack>
                  }
                >
                  <XStack
                    ai="center"
                    gap={6}
                    $gtSm={{ gap: 8 }}
                    $gtMd={{ gap: 10 }}
                    style={{
                      cursor: pathwayOptions.length ? "pointer" : "default",
                    }}
                  >
                    <EyebrowText
                      color="#987451"
                      ta="left"
                    >
                      Skills Pathways
                    </EyebrowText>
                    {pathwayOptions.length ? (
                      <View
                        style={{
                          transform: [
                            { rotate: pathwayMenuOpen ? "180deg" : "0deg" },
                          ],
                          // @ts-ignore web CSS
                          transition: "transform 180ms ease-in-out",
                        }}
                      >
                        <ChevronDown
                          width={media.gtMd ? 18 : media.gtSm ? 16 : 14}
                          height={media.gtMd ? 18 : media.gtSm ? 16 : 14}
                          color="#987451"
                        />
                      </View>
                    ) : null}
                  </XStack>
                </Popover>
              </YStack>
            </XStack>

            <YStack w="100%" gap="$6">
              {listLoading ? (
                <YStack
                  w="100%"
                  minHeight={280}
                  ai="center"
                  jc="center"
                  py="$8"
                >
                  <CoursePagePremiumLoader variant="pathway" />
                </YStack>
              ) : listMatchesRoute && !pathwayHasCourses ? (
                <YStack
                  w="100%"
                  minHeight={200}
                  ai="center"
                  jc="center"
                  py="$8"
                  px="$4"
                >
                  <BodyText
                    ta="center"
                    color="#5C5854"
                    maxWidth={480}
                  >
                    No courses are available in this pathway yet.
                  </BodyText>
                </YStack>
              ) : listMatchesRoute ? (
                (["beginner", "intermediate", "advanced"] as SkillLevel[]).map(
                  (level) => {
                    const courses = coursesByLevel[level];
                    if (!courses.length) return null;

                    const gradientColors: [string, string] =
                      level === "beginner"
                        ? ["#FFFFFF", "#DEFAEB"]
                        : level === "intermediate"
                          ? ["#FFFFFF", "#FEF3C6"]
                          : ["#FFFFFF", "#DDD6FF"];

                    return (
                      <YStack key={level} gap="$3">
                        <YStack
                          w="100%"
                          br={10}
                          overflow="hidden"
                          boxShadow="0 2px 4px rgba(0,0,0,0.12), 0 0 2px rgba(0,0,0,0.08)"
                        >
                          <LinearGradient
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            colors={gradientColors}
                            style={{
                              paddingLeft: 24,
                              paddingRight: 0,
                              flexDirection: "row",
                              alignItems: "stretch",
                              height: 64,
                            }}
                          >
                            <XStack
                              flex={1}
                              jc="space-between"
                              ai="center"
                              pr={12}
                              onPress={() => toggleLevel(level)}
                              style={{ cursor: "pointer" }}
                            >
                              <ContentTitle>
                                {levelLabel(level)}
                              </ContentTitle>
                              <AnimatedChevron open={expanded[level]} />
                            </XStack>
                            <YStack
                              w={8}
                              h="100%"
                              bg={
                                level === "beginner"
                                  ? "rgba(190, 244, 215, 1)" // #BEF4D7
                                  : level === "intermediate"
                                    ? "rgba(254, 230, 133, 1)" // #FEE685
                                    : "rgba(196, 180, 255, 1)" // #C4B4FF
                              }
                              borderTopRightRadius={10}
                              borderBottomRightRadius={10}
                            />
                          </LinearGradient>
                        </YStack>
                        <Collapsible open={expanded[level]}>
                          <YStack gap={32} pt={40}>
                            {courses.map((course) => (
                              <PathwayCourseCard
                                key={course.id}
                                course={course}
                                progressPercentage={
                                  courseProgressMap[course.id] != null
                                    ? courseProgressMap[course.id] * 100
                                    : undefined
                                }
                                href={`/courses/${encodeURIComponent(course.slug)}`}
                              />
                            ))}
                          </YStack>
                        </Collapsible>
                      </YStack>
                    );
                  },
                )
              ) : (
                <YStack
                  w="100%"
                  minHeight={200}
                  ai="center"
                  jc="center"
                  py="$8"
                  px="$4"
                >
                  <BodyText
                    ta="center"
                    color="#5C5854"
                    maxWidth={480}
                  >
                    This skills pathway could not be loaded. Check the link or
                    try again later.
                  </BodyText>
                </YStack>
              )}
            </YStack>
          </YStack>
        </ContentWrapper>
      </ScrollView>
    </YStack>
  );
}
